/**
 * RouteHints — friendly, modern productivity nudges per route.
 *
 * Renders a compact glass-card popup in the bottom-left corner (out of
 * the way of the right-edge Quick Question tab) with a single tip,
 * an optional CTA, and a soft slide-in / slide-out animation. One tip
 * per route per session — dismissed for the rest of the session on
 * click of the ✕ button.
 *
 * Zero LLM cost. Zero backend calls. Pure client-side polish.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  X, Sparkles, Zap, FileText, Wrench, Plug, Sunrise, Home as HomeIcon,
  Trophy, Gift, MessageCircle, CreditCard, ChevronRight,
} from "lucide-react";

// Deliberately small tip library — one per key route. Order matters:
// first match wins so we can shadow specific paths above generic ones.
const HINTS = [
  { match: (p) => p === "/dashboard",              icon: Sparkles, tone: "emerald",
    title: "Pro tip",
    body: "Turn on Late-Rent Autopilot per lease and we'll handle reminders + draft your N4 automatically.",
    cta: { label: "Set it up", to: "/late-rent-autopilot" } },
  { match: (p) => p === "/late-rent-autopilot",    icon: Zap,      tone: "amber",
    title: "One click to start",
    body: "Flip a lease to On below — we'll send the first reminder within minutes of a missed payment.",
    cta: null },
  { match: (p) => p === "/leases",                 icon: FileText, tone: "indigo",
    title: "60-second lease",
    body: "Use the Lease Generator to produce a province-compliant document in about a minute.",
    cta: null },
  { match: (p) => p === "/tickets",                icon: Wrench,   tone: "rose",
    title: "Assign in two taps",
    body: "Open a ticket and pick a vendor from your Rolodex — we track the whole thread from there.",
    cta: null },
  { match: (p) => p === "/integrations",           icon: Plug,     tone: "violet",
    title: "Missing an integration?",
    body: "Vote on the Coming Soon list — the most-requested ones ship first.",
    cta: null },
  { match: (p) => p === "/briefing",               icon: Sunrise,  tone: "amber",
    title: "60-second briefing",
    body: "Hit Play on the audio briefing — a natural human pace, perfect while making coffee.",
    cta: null },
  { match: (p) => p === "/tenant-community" || p.startsWith("/tenant-community?"), icon: HomeIcon, tone: "emerald",
    title: "Find your next place",
    body: "Switch to Rentals to browse listings from real landlords across every province.",
    cta: { label: "Open Rentals", to: "/tenant-community?tab=listings" } },
  { match: (p) => p === "/payments",               icon: CreditCard, tone: "sky",
    title: "Set it, forget it",
    body: "Turn on auto-pay to protect your rent-on-time score every month automatically.",
    cta: null },
  { match: (p) => p === "/rent-score",             icon: Trophy,   tone: "amber",
    title: "Share your badge",
    body: "After a few on-time payments, share your score to jump to the top of landlord shortlists.",
    cta: null },
  { match: (p) => p === "/referrals" || p === "/share-and-earn", icon: Gift, tone: "emerald",
    title: "Earn CA$50",
    body: "Share your referral link — you earn CA$50 for every landlord friend who subscribes.",
    cta: null },
  { match: (p) => p === "/messages",               icon: MessageCircle, tone: "sky",
    title: "Reply Copilot",
    body: "Long-press a message to draft a reply in your voice with one tap.",
    cta: null },
];

// Palette per tone — tuned so the glass card reads friendly, not shouty.
const TONES = {
  emerald: { icon: "text-emerald-600 bg-emerald-100", ring: "ring-emerald-200", accent: "bg-emerald-600 hover:bg-emerald-700 text-white" },
  amber:   { icon: "text-amber-700 bg-amber-100",     ring: "ring-amber-200",   accent: "bg-amber-500 hover:bg-amber-600 text-white" },
  sky:     { icon: "text-sky-600 bg-sky-100",         ring: "ring-sky-200",     accent: "bg-sky-600 hover:bg-sky-700 text-white" },
  rose:    { icon: "text-rose-600 bg-rose-100",       ring: "ring-rose-200",    accent: "bg-rose-600 hover:bg-rose-700 text-white" },
  violet:  { icon: "text-violet-600 bg-violet-100",   ring: "ring-violet-200",  accent: "bg-violet-600 hover:bg-violet-700 text-white" },
  indigo:  { icon: "text-indigo-600 bg-indigo-100",   ring: "ring-indigo-200",  accent: "bg-indigo-600 hover:bg-indigo-700 text-white" },
};

const SS_KEY = "crc_route_hints_shown";

function alreadyShown(pathname) {
  try {
    const raw = sessionStorage.getItem(SS_KEY);
    const set = raw ? new Set(JSON.parse(raw)) : new Set();
    return set.has(pathname);
  } catch { return false; }
}

function markShown(pathname) {
  try {
    const raw = sessionStorage.getItem(SS_KEY);
    const set = raw ? new Set(JSON.parse(raw)) : new Set();
    set.add(pathname);
    sessionStorage.setItem(SS_KEY, JSON.stringify([...set]));
  } catch { /* ignore */ }
}

export default function RouteHints() {
  const loc = useLocation();
  const [hint, setHint] = useState(null);     // matched hint object
  const [visible, setVisible] = useState(false); // controls slide-in/out
  const timerIn = useRef(null);
  const timerOut = useRef(null);

  const pathname = loc.pathname;

  const match = useMemo(() => {
    if (alreadyShown(pathname)) return null;
    return HINTS.find((h) => { try { return h.match(pathname); } catch { return false; } }) || null;
  }, [pathname]);

  useEffect(() => {
    // Cancel any pending transitions from the previous route.
    if (timerIn.current)  clearTimeout(timerIn.current);
    if (timerOut.current) clearTimeout(timerOut.current);
    if (!match) { setVisible(false); setHint(null); return; }

    setHint(match);
    // Small delay so the toast doesn't fight the page's own entrance
    // animation for the user's attention.
    timerIn.current = setTimeout(() => setVisible(true), 700);
    // Auto-dismiss after 10s to keep the UI clean.
    timerOut.current = setTimeout(() => {
      setVisible(false);
      markShown(pathname);
      // Fully unmount after the fade so screen readers stop announcing.
      setTimeout(() => setHint(null), 300);
    }, 10700);
    return () => {
      if (timerIn.current) clearTimeout(timerIn.current);
      if (timerOut.current) clearTimeout(timerOut.current);
    };
  }, [match, pathname]);

  function dismiss() {
    setVisible(false);
    markShown(pathname);
    setTimeout(() => setHint(null), 300);
  }

  if (!hint) return null;
  const Icon = hint.icon;
  const t = TONES[hint.tone] || TONES.emerald;

  return (
    <div
      className={`hidden md:block fixed lg:left-72 left-4 bottom-4 z-40 print:hidden max-w-[340px] transition-all duration-300 ease-out ${
        visible
          ? "opacity-100 translate-y-0"
          : "opacity-0 translate-y-3 pointer-events-none"
      }`}
      data-testid="route-hint"
      role="status"
      aria-live="polite"
    >
      {/* Glass card — subtle white/backdrop-blur so it reads as a
          friendly whisper, not a modal. */}
      <div className={`relative rounded-2xl bg-white/95 backdrop-blur border border-slate-200 shadow-xl ring-1 ${t.ring} overflow-hidden`}>
        {/* Progress bar — quiet countdown so the user knows it'll go
            away on its own. Purely visual, no logic. */}
        <div
          className={`absolute inset-x-0 top-0 h-0.5 ${t.accent} origin-left`}
          style={{
            animation: visible ? "crc-hint-progress 10s linear forwards" : "none",
          }}
        />
        <div className="p-3.5 pr-9 flex gap-3">
          <div className={`shrink-0 w-9 h-9 rounded-xl flex items-center justify-center ${t.icon}`}>
            <Icon className="w-4 h-4" strokeWidth={2.4}/>
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500">
                {hint.title}
              </div>
            </div>
            <p className="mt-1 text-[13px] leading-relaxed text-slate-700">
              {hint.body}
            </p>
            {hint.cta?.to && hint.cta?.label && (
              <Link
                to={hint.cta.to}
                onClick={dismiss}
                data-testid="route-hint-cta"
                className={`mt-2.5 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-[12px] font-bold ${t.accent} transition-colors`}
              >
                {hint.cta.label} <ChevronRight className="w-3.5 h-3.5" strokeWidth={2.6}/>
              </Link>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          data-testid="route-hint-dismiss"
          className="absolute top-2 right-2 rounded-full p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
        >
          <X className="w-3.5 h-3.5" strokeWidth={2.4}/>
        </button>
      </div>
      {/* Keyframes local to this component so we don't touch global CSS. */}
      <style>{`
        @keyframes crc-hint-progress {
          from { transform: scaleX(1); opacity: 0.35; }
          to   { transform: scaleX(0); opacity: 0.15; }
        }
      `}</style>
    </div>
  );
}
